/* ════════════════════════════════════════════════════════════════════════
   CLEANING DATA — rooms, room-by-room checklists, daily reset, weekly
   rotation, monthly deep-clean, seasonal reset. All mock; persistence is
   handled by HomeOSStateProvider.
   ════════════════════════════════════════════════════════════════════════ */

import { ROOM_LIST } from "../config/homeOsTheme";

/* Frequency, age suitability, energy required, estimated minutes, status. */
const make = (room, title, frequency, energy, age, mins) => ({
  id: `${room}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`,
  title, room, frequency, energy, age, estimatedMinutes: mins,
  assignedTo: null, status: "Upcoming",
});

export const CLEANING_TASKS = [
  /* Kitchen */
  make("kitchen", "Clear benches",            "Daily",   "Low", "Teen", 5),
  make("kitchen", "Wash dishes / dishwasher", "Daily",   "Low", "Teen", 10),
  make("kitchen", "Wipe benches",             "Daily",   "Low", "Child", 5),
  make("kitchen", "Wipe cupboard fronts",     "Weekly",  "Medium", "Teen", 15),
  make("kitchen", "Clean sink",               "Daily",   "Low", "Teen", 5),
  make("kitchen", "Clean stovetop",           "Weekly",  "Medium", "Adult", 15),
  make("kitchen", "Clean microwave",          "Weekly",  "Low", "Teen", 10),
  make("kitchen", "Clean fridge shelves",     "Monthly", "Medium", "Adult", 30),
  make("kitchen", "Check expired food",       "Weekly",  "Low", "Adult", 10),
  make("kitchen", "Sweep / vacuum floor",     "Daily",   "Low", "Teen", 10),
  make("kitchen", "Mop floor",                "Weekly",  "Medium", "Adult", 15),
  make("kitchen", "Empty rubbish",            "Daily",   "Low", "Child", 5),
  make("kitchen", "Restock dish soap / cloths","Weekly", "Low", "Adult", 5),

  /* Bathroom */
  make("bathroom", "Clean toilet",         "Weekly",  "Medium", "Adult", 10),
  make("bathroom", "Clean basin",          "Weekly",  "Low",    "Teen",  10),
  make("bathroom", "Clean mirror",         "Weekly",  "Low",    "Teen",   5),
  make("bathroom", "Wipe surfaces",        "Daily",   "Low",    "Teen",   5),
  make("bathroom", "Scrub shower / bath",  "Weekly",  "Medium", "Adult", 15),
  make("bathroom", "Replace towels",       "Weekly",  "Low",    "Teen",   5),
  make("bathroom", "Empty bin",            "Daily",   "Low",    "Child",  5),
  make("bathroom", "Mop floor",            "Weekly",  "Medium", "Adult", 10),
  make("bathroom", "Restock toilet paper", "Weekly",  "Low",    "Teen",   5),
  make("bathroom", "Restock soap",         "Weekly",  "Low",    "Teen",   5),

  /* Living */
  make("living", "Put items away",       "Daily",  "Low",    "Child", 10),
  make("living", "Dust surfaces",        "Weekly", "Low",    "Teen",  10),
  make("living", "Vacuum couch",         "Weekly", "Medium", "Teen",  10),
  make("living", "Vacuum floor",         "Weekly", "Medium", "Adult", 15),
  make("living", "Wipe remotes/devices", "Weekly", "Low",    "Teen",   5),
  make("living", "Fold blankets",        "Daily",  "Low",    "Child",  5),
  make("living", "Reset cushions",       "Daily",  "Low",    "Child",  3),
  make("living", "Clear rubbish",        "Daily",  "Low",    "Child",  5),

  /* Bedroom */
  make("bedroom", "Make bed",             "Daily",   "Low",    "Child", 3),
  make("bedroom", "Change sheets",        "Weekly",  "Medium", "Teen",  15),
  make("bedroom", "Put clothes away",     "Daily",   "Low",    "Child", 10),
  make("bedroom", "Clear bedside table",  "Weekly",  "Low",    "Teen",  5),
  make("bedroom", "Dust surfaces",        "Weekly",  "Low",    "Teen",  10),
  make("bedroom", "Vacuum floor",         "Weekly",  "Medium", "Teen",  10),
  make("bedroom", "Empty bin",            "Weekly",  "Low",    "Child", 3),
  make("bedroom", "Reset room",           "Daily",   "Low",    "Child", 10),

  /* Laundry */
  make("laundry", "Sort washing",         "Daily",   "Low",    "Teen",  5),
  make("laundry", "Wash load",            "Daily",   "Low",    "Teen",  5),
  make("laundry", "Dry load",             "Daily",   "Low",    "Teen",  5),
  make("laundry", "Fold clean clothes",   "Daily",   "Low",    "Teen",  15),
  make("laundry", "Put away clothes",     "Daily",   "Low",    "Child", 10),
  make("laundry", "Wipe machines",        "Weekly",  "Low",    "Adult", 5),
  make("laundry", "Clear lint filter",    "Weekly",  "Low",    "Teen",  3),
  make("laundry", "Restock detergent",    "Weekly",  "Low",    "Adult", 5),

  /* Entryway */
  make("entryway", "Put shoes away",       "Daily",  "Low", "Child", 3),
  make("entryway", "Hang bags / coats",    "Daily",  "Low", "Child", 3),
  make("entryway", "Wipe surfaces",        "Weekly", "Low", "Teen",  5),
  make("entryway", "Sweep / vacuum floor", "Weekly", "Low", "Teen",  10),
  make("entryway", "Sort incoming mail",   "Daily",  "Low", "Adult", 5),
  make("entryway", "Reset keys/wallets",   "Daily",  "Low", "Adult", 2),

  /* Office */
  make("office", "Clear desk",         "Daily",   "Low", "Adult", 5),
  make("office", "File papers",        "Weekly",  "Low", "Adult", 10),
  make("office", "Empty bin",          "Weekly",  "Low", "Teen", 3),
  make("office", "Wipe keyboard/mouse","Weekly",  "Low", "Adult", 5),
  make("office", "Dust surfaces",      "Weekly",  "Low", "Teen", 5),
  make("office", "Sort cables",        "Monthly", "Medium", "Adult", 15),
  make("office", "Vacuum floor",       "Weekly",  "Medium", "Adult", 10),

  /* Garage */
  make("garage", "Clear walkway",       "Weekly",  "Medium", "Adult", 10),
  make("garage", "Sort tools",          "Monthly", "Medium", "Adult", 30),
  make("garage", "Sweep floor",         "Weekly",  "Medium", "Teen",  15),
  make("garage", "Check storage boxes", "Monthly", "Low",    "Adult", 15),
  make("garage", "Remove rubbish",      "Weekly",  "Medium", "Adult", 10),
  make("garage", "Check safety hazards","Monthly", "Low",    "Adult", 10),

  /* Storage */
  make("storage", "Label boxes",          "Seasonal","Low", "Adult", 30),
  make("storage", "Remove unused items",  "Seasonal","Medium", "Adult", 60),
  make("storage", "Reorganise shelves",   "Seasonal","Medium", "Adult", 45),
  make("storage", "Check seasonal items", "Seasonal","Low", "Adult", 20),
  make("storage", "Create donation pile", "Seasonal","Medium", "Adult", 30),

  /* Outdoor */
  make("outdoor", "Clear entry path",      "Weekly",  "Medium", "Teen", 10),
  make("outdoor", "Put outdoor items away","Daily",   "Low",    "Child", 5),
  make("outdoor", "Sweep deck / patio",    "Weekly",  "Medium", "Teen", 15),
  make("outdoor", "Empty outdoor bins",    "Weekly",  "Low",    "Adult", 5),
  make("outdoor", "Check garden tools",    "Monthly", "Low",    "Adult", 10),
  make("outdoor", "Check hazards",         "Monthly", "Low",    "Adult", 10),

  /* Playroom */
  make("playroom", "Sort toys",            "Daily",   "Low", "Child", 10),
  make("playroom", "Put toys in bins",     "Daily",   "Low", "Child", 5),
  make("playroom", "Wipe surfaces",        "Weekly",  "Low", "Teen", 5),
  make("playroom", "Vacuum floor",         "Weekly",  "Medium", "Teen", 10),
  make("playroom", "Remove broken toys",   "Monthly", "Low", "Adult", 10),
  make("playroom", "Create donation pile", "Seasonal","Low", "Adult", 20),
  make("playroom", "Reset activity area",  "Daily",   "Low", "Child", 5),
];

/* Default room metadata (status, last cleaned, owner) */
export const ROOM_STATE = ROOM_LIST.map(r => ({
  id: r.id,
  label: r.label,
  owner: null,
  lastCleaned: null,           // ISO date string
  nextDeepClean: null,
}));

/* Daily reset = a small subset of high-frequency tasks */
export const DAILY_RESET_IDS = [
  "kitchen-clear-benches",
  "kitchen-wipe-benches",
  "kitchen-wash-dishes-dishwasher",
  "kitchen-empty-rubbish",
  "laundry-sort-washing",
  "entryway-put-shoes-away",
  "living-reset-cushions",
];

/* Weekly rotation by day-of-week */
export const WEEKLY_ROTATION = [
  { day: "Mon", room: "kitchen",  label: "Kitchen" },
  { day: "Tue", room: "bathroom", label: "Bathroom" },
  { day: "Wed", room: "laundry",  label: "Laundry" },
  { day: "Thu", room: "bedroom",  label: "Bedrooms" },
  { day: "Fri", room: "living",   label: "Living areas" },
  { day: "Sat", room: "outdoor",  label: "Outdoor / Garage" },
  { day: "Sun", room: null,       label: "Rest / light reset" },
];

export const FREQUENCIES = ["Daily", "Weekly", "Monthly", "Seasonal"];
